import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ATHENS_GRAY } from "@/shared/colors";
import { Product, PRODUCT_AREA_ASSET_ID } from "@/shared/models/Product";
import { getProducts } from "@/shared/repositories/ProductRepository";
import { getCurrentLineShiftId } from "@/shared/repositories/ShiftRepository";
import {
	saveWeldTest,
	slotPhotoFile,
	uploadWeldTestPhoto,
} from "@/shared/repositories/WeldTestRepository";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	EmployeeField,
	EmployeeNumberProvider,
	useEmployeeNumber,
} from "@/pages/saw-tablet/employee/EmployeeNumber";
import { BendResult, BendTestResult } from "./components/BendTestResult";
import { LineSelector } from "./components/LineSelector";
import { PhotoCapture } from "./components/PhotoCapture";

const InfoField = ({
	label,
	value,
}: {
	label: string;
	value: string | null | undefined;
}) => (
	<Box>
		<Typography variant="caption" color="text.secondary" fontWeight={600}>
			{label}
		</Typography>
		<Typography variant="body1" fontWeight={500}>
			{value || "—"}
		</Typography>
	</Box>
);

export const WeldTestPage = () => {
	const [assetId, setAssetId] = useState<number | null>(null);
	const [shiftError, setShiftError] = useState("");
	const [dismissedError, setDismissedError] = useState("");
	const { data: shiftId } = useQuery<number | null, Error>({
		queryKey: ["currentLineShiftId", assetId],
		queryFn: () => getCurrentLineShiftId(assetId as number),
		enabled: assetId !== null,
		refetchInterval: 60_000,
		onError: (e) => setShiftError(e.message),
		onSuccess: () => {
			setShiftError("");
			setDismissedError("");
		},
	});

	return (
		<EmployeeNumberProvider shiftId={shiftId}>
			<WeldTestForm assetId={assetId} setAssetId={setAssetId} />
			<ErrorModal
				error={shiftError === dismissedError ? "" : shiftError}
				onHide={() => setDismissedError(shiftError)}
			/>
		</EmployeeNumberProvider>
	);
};

const WeldTestForm = ({
	assetId,
	setAssetId,
}: {
	assetId: number | null;
	setAssetId: (assetId: number | null) => void;
}) => {
	const { requireEmployee } = useEmployeeNumber();
	const [product, setProduct] = useState<Product | null>(null);
	const [bend1, setBend1] = useState<BendResult | null>(null);
	const [bend2, setBend2] = useState<BendResult | null>(null);
	const [photo1, setPhoto1] = useState<File | null>(null);
	const [photo2, setPhoto2] = useState<File | null>(null);
	const [notes, setNotes] = useState("");
	const [notification, setNotification] = useState("");
	const [error, setError] = useState("");

	const { data: allProducts, isLoading } = useQuery<Product[], Error>({
		queryKey: ["products"],
		queryFn: () => getProducts(),
	});

	const weldProducts = useMemo(
		() =>
			(allProducts ?? []).filter(
				(p) => p.AreaAssetId === PRODUCT_AREA_ASSET_ID.weld,
			),
		[allProducts],
	);

	const resetForm = () => {
		setProduct(null);
		setBend1(null);
		setBend2(null);
		setPhoto1(null);
		setPhoto2(null);
		setNotes("");
	};

	const saveMutation = useMutation({
		mutationFn: async (employee: string) => {
			const path1 = photo1
				? await uploadWeldTestPhoto(slotPhotoFile(photo1, 1))
				: null;
			const path2 = photo2
				? await uploadWeldTestPhoto(slotPhotoFile(photo2, 2))
				: null;
			return saveWeldTest({
				assetId: assetId as number,
				productId: product?.ProductId ?? null,
				bendTest1Passed: bend1 === "OK",
				bendTest2Passed: bend2 === "OK",
				photo1Path: path1,
				photo2Path: path2,
				notes: notes.trim() || null,
				operator: employee,
			});
		},
		onSuccess: () => {
			setNotification("Weld test saved");
			resetForm();
		},
		onError: (e: unknown) =>
			setError(e instanceof Error ? e.message : "Error saving weld test"),
	});

	const canSave =
		assetId !== null &&
		!!product &&
		!!bend1 &&
		!!bend2 &&
		!saveMutation.isLoading;

	return (
		<>
			<HelmetDexteel title="Weldline Test" />
			<Box
				sx={{
					minHeight: "calc(100vh - 64px)",
					backgroundColor: ATHENS_GRAY,
					px: { xs: 1.5, md: 3 },
					py: { xs: 1.5, md: 2 },
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					spacing={1.5}
					sx={{ mb: 1.5 }}
				>
					<Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
						Weldline Test
					</Typography>
					<EmployeeField />
				</Stack>

				<Grid2 container spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
					{/* Left: form */}
					<Grid2 size={{ xs: 12, md: 7 }}>
						<Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
							<Stack spacing={2}>
								{/* Line selector */}
								<Box>
									<Typography
										variant="subtitle2"
										fontWeight={600}
										sx={{ mb: 0.75 }}
									>
										Line
									</Typography>
									<LineSelector
										value={assetId}
										onChange={setAssetId}
										areaAssetId={PRODUCT_AREA_ASSET_ID.weld}
									/>
								</Box>

								{/* Product selector */}
								<Autocomplete
									options={weldProducts}
									loading={isLoading}
									value={product}
									onChange={(_, next) => setProduct(next)}
									getOptionLabel={(p) =>
										p.ExtendedPartNumber
											? `${p.PartNumber} (${p.ExtendedPartNumber})`
											: p.PartNumber
									}
									renderOption={(props, option) => (
										<li {...props} key={option.ProductId}>
											{`${option.PartNumber}${
												option.ExtendedPartNumber
													? ` (${option.ExtendedPartNumber})`
													: ""
											}${option.CustomerName ? ` — ${option.CustomerName}` : ""}`}
										</li>
									)}
									isOptionEqualToValue={(a, b) => a.ProductId === b.ProductId}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Part Number"
											size="medium"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isLoading ? (
															<CircularProgress color="inherit" size={20} />
														) : (
															params.InputProps.endAdornment
														)}
													</>
												),
											}}
										/>
									)}
								/>

								{/* Product info */}
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 6, sm: 3 }}>
										<InfoField
											label="Part Number"
											value={product?.PartNumber}
										/>
									</Grid2>
									<Grid2 size={{ xs: 6, sm: 3 }}>
										<InfoField
											label="Extended Part Number"
											value={product?.ExtendedPartNumber}
										/>
									</Grid2>
									<Grid2 size={{ xs: 6, sm: 3 }}>
										<InfoField
											label="Customer Part Number"
											value={product?.CustomerPartNumber}
										/>
									</Grid2>
									<Grid2 size={{ xs: 6, sm: 3 }}>
										<InfoField label="Customer" value={product?.CustomerName} />
									</Grid2>
								</Grid2>

								<Divider />

								{/* Bend tests */}
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6 }}>
										<BendTestResult
											label="Bend Test 1"
											value={bend1}
											onChange={setBend1}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6 }}>
										<BendTestResult
											label="Bend Test 2"
											value={bend2}
											onChange={setBend2}
										/>
									</Grid2>
								</Grid2>

								{/* Notes */}
								<TextField
									label="Notes / Comments"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									fullWidth
									multiline
									minRows={3}
								/>
							</Stack>
						</Paper>
					</Grid2>

					{/* Right: photo captures */}
					<Grid2 size={{ xs: 12, md: 5 }}>
						<Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: "100%" }}>
							<Stack spacing={2}>
								<PhotoCapture
									label="Bend Test 1 — Photo"
									file={photo1}
									onChange={setPhoto1}
								/>
								<PhotoCapture
									label="Bend Test 2 — Photo"
									file={photo2}
									onChange={setPhoto2}
								/>
							</Stack>
						</Paper>
					</Grid2>
				</Grid2>

				{/* Save — anchored at the very bottom of the page (not sticky) */}
				<Box sx={{ mt: 2, py: 1.5 }}>
					<Button
						variant="contained"
						size="large"
						fullWidth
						disabled={!canSave}
						onClick={() => requireEmployee(saveMutation.mutate)}
						startIcon={
							saveMutation.isLoading ? (
								<CircularProgress color="inherit" size={20} />
							) : undefined
						}
						sx={{ py: 1.5, fontSize: "1.1rem", fontWeight: 600 }}
					>
						{saveMutation.isLoading ? "Saving…" : "Save Weld Test"}
					</Button>
				</Box>
			</Box>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
